import { AddIcon } from "@minket/brand/icons/add";
import { MoreIcon } from "@minket/brand/icons/more";
import { ProjectsIcon } from "@minket/brand/icons/projects";
import { Button } from "@minket/ui/components/button";
import { EmptyState } from "@minket/ui/components/empty-state";
import { StatusBadge } from "@minket/ui/components/status-badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@minket/ui/components/table";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { lazy, Suspense, startTransition, useState } from "react";
import { useRecorded } from "@/components/audit/recorded";
import { count, stamp } from "@/components/ops/format";
import { When } from "@/components/ops/when";
import { UnavailableAction } from "@/components/page/action";
import { GhostTable } from "@/components/page/ghost";
import { SubPage } from "@/components/page/page";
import { OrgScope } from "@/components/page/scope";
import { QueryView } from "@/components/page/states";
import type { ProjectAct, ProjectMenuProps } from "@/components/tenancy/project-menu.ondemand";
import { killSwitchFigures } from "@/components/tenancy/project-sheet";
import type { ProjectBody, ProjectUpdate } from "@/components/tenancy/settings.ondemand";
import { endpoints } from "@/lib/endpoints";
import { useCan } from "@/lib/permissions";
import { type Project, projectsQuery } from "@/lib/queries";
import { apiKey, apiQuery, apiRequest } from "@/lib/query";
import { displayName } from "@/lib/session";
import { notify } from "@/lib/toast";
import { useWorkspace } from "@/lib/workspace";

export const Route = createFileRoute("/settings/projects")({
  component: ProjectsSettings,
});

const loadDialogs = () => import("@/components/tenancy/settings.ondemand");
const loadSign = () => import("@/components/tenancy/countersign.ondemand");
const NewProjectDialog = lazy(() => loadDialogs().then((m) => ({ default: m.NewProjectDialog })));
const EditProjectDialog = lazy(() => loadDialogs().then((m) => ({ default: m.EditProjectDialog })));
const Sign = lazy(() => loadSign().then((m) => ({ default: m.Sign })));

const columns = [
  "Project",
  "Slug",
  "Environments",
  "Streams",
  "Daily cap",
  "Kill switch",
  "Created, UTC",
] as const;
const widths = [undefined, "9rem", "11rem", "12rem", "7rem", "10rem", "9rem"];
const streamLabels = { transactional: "Transactional", marketing: "Marketing" } as const;

const ghost = () => <GhostTable columns={columns} widths={widths} label="projects" />;

function ProjectsSettings() {
  const { organization, selectProject } = useWorkspace();
  const orgId = organization?.organization_id ?? "";
  const canManage = useCan("projects.manage");
  const queryClient = useQueryClient();
  const [creating, setCreating] = useState(false);
  const create = useMutation({
    mutationFn: (body: ProjectBody) => apiRequest(endpoints.createProject, { orgId }, { body }),
    onSuccess: (project) => {
      void queryClient.invalidateQueries({ queryKey: apiKey(endpoints.projects, { orgId }) });
      selectProject(project.id);
      notify("success", `${project.name} created, with production and preview environments.`);
      setCreating(false);
    },
  });
  const action = !organization ? undefined : canManage ? (
    <Button
      onClick={() => {
        create.reset();
        setCreating(true);
      }}
      onPointerEnter={() => void loadDialogs()}
      onFocus={() => void loadDialogs()}
    >
      <AddIcon size={20} aria-hidden />
      New project
    </Button>
  ) : (
    <UnavailableAction label="New project" icon={AddIcon} reason="Admins and owners add projects" />
  );
  return (
    <SubPage title="Projects" action={action}>
      <OrgScope
        will="Its projects appear here."
        icon={<ProjectsIcon />}
        preview={ghost}
        headingLevel={3}
      >
        {(org) => <ProjectList orgId={org.organization_id} canManage={canManage} />}
      </OrgScope>
      {creating && organization ? (
        <Suspense fallback={null}>
          <NewProjectDialog
            organizationName={organization.name}
            create={create}
            onClose={() => setCreating(false)}
          />
        </Suspense>
      ) : null}
    </SubPage>
  );
}

type Act = ProjectAct;

function ProjectList({ orgId, canManage }: { orgId: string; canManage: boolean }) {
  const query = useQuery(projectsQuery(orgId));
  const queryClient = useQueryClient();
  const { user } = useWorkspace();
  const recorded = useRecorded();
  const [editing, setEditing] = useState<Project | null>(null);
  const [acting, setActing] = useState<{ act: Act; project: Project } | null>(null);
  const refresh = () =>
    void queryClient.invalidateQueries({ queryKey: apiKey(endpoints.projects, { orgId }) });
  const save = useMutation({
    mutationFn: (body: ProjectUpdate) =>
      apiRequest(endpoints.updateProject, { projectId: editing?.id ?? "" }, { body }),
    onSuccess: (project) => {
      refresh();
      notify("success", `${project.name} saved.`);
      setEditing(null);
    },
  });
  const act = useMutation({
    mutationFn: async ({ act, project }: { act: Act; project: Project }) => {
      const projectId = project.id;
      if (act === "rotate") await apiRequest(endpoints.rotateSigningSecret, { projectId });
      else if (act === "delete") await apiRequest(endpoints.deleteProject, { projectId });
      else {
        await apiRequest(
          endpoints.updateProject,
          { projectId },
          { body: { kill_switch: act === "pause" ? "manual" : "off" } },
        );
      }
    },
    onSuccess: (_, { act, project }) => {
      refresh();
      setActing(null);
      const project_id = project.id;
      if (act === "rotate") {
        recorded("Signing secret rotated.", {
          action: "project.signing_secret_rotate",
          project_id,
        });
      } else if (act === "delete") {
        recorded(`${project.name} deleted.`, { action: "project.delete" });
      } else {
        recorded(act === "pause" ? "Sending paused." : "Sending resumed.", {
          action: "project.kill_switch",
          project_id,
        });
      }
    },
  });
  const open = (next: Act, project: Project) => {
    act.reset();
    setActing({ act: next, project });
  };
  return (
    <QueryView query={query} noun="projects" preview={ghost} headingLevel={3}>
      {(page) =>
        page.data.length === 0 ? (
          <EmptyState
            data-state="empty"
            icon={<ProjectsIcon />}
            title="No projects yet"
            description="Create one; its domains, keys and audience live inside it."
            headingLevel={3}
            preview={ghost()}
          />
        ) : (
          <>
            <Table aria-label="Projects" className="min-w-[58rem]">
              <colgroup>
                {widths.map((width, index) => (
                  <col key={columns[index]} style={{ width }} />
                ))}
              </colgroup>
              <TableHeader>
                <TableRow>
                  {columns.map((column, index) => (
                    <TableHead
                      key={column}
                      className={index >= 4 && index <= 5 ? "text-right" : undefined}
                    >
                      {column}
                    </TableHead>
                  ))}
                  <TableHead className="w-0">
                    <span className="sr-only">Actions</span>
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {page.data.map((project) => (
                  <ProjectRow
                    key={project.id}
                    project={project}
                    canManage={canManage}
                    onEdit={() => {
                      save.reset();
                      setEditing(project);
                    }}
                    onAct={(next) => open(next, project)}
                  />
                ))}
              </TableBody>
            </Table>
            {editing ? (
              <Suspense fallback={null}>
                <EditProjectDialog project={editing} save={save} onClose={() => setEditing(null)} />
              </Suspense>
            ) : null}
            {acting ? (
              <Suspense fallback={null}>
                <Sign
                  {...signFor(acting.act, acting.project)}
                  pending={act.isPending}
                  error={act.error}
                  signer={displayName(user)}
                  onConfirm={() => act.mutate(acting)}
                  onClose={() => setActing(null)}
                />
              </Suspense>
            ) : null}
          </>
        )
      }
    </QueryView>
  );
}

function ProjectRow({
  project,
  canManage,
  onEdit,
  onAct,
}: {
  project: Project;
  canManage: boolean;
  onEdit: () => void;
  onAct: (act: Act) => void;
}) {
  const queryClient = useQueryClient();
  const environments = useQuery(apiQuery(endpoints.environments, { projectId: project.id }));
  const addPreview = useMutation({
    mutationFn: () =>
      apiRequest(
        endpoints.createEnvironment,
        { projectId: project.id },
        {
          body: { slug: "preview" },
        },
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: apiKey(endpoints.environments, { projectId: project.id }),
      });
      notify("success", "Preview environment added.");
    },
  });
  const list = environments.data?.data ?? [];
  const hasPreview = list.some((environment) => environment.slug === "preview");
  const paused = project.kill_switch.state !== "off";
  return (
    <TableRow className="[&>td]:py-3 [&>td]:align-middle">
      <TableCell className="font-semibold wrap-break-word">{project.name}</TableCell>
      <TableCell className="type-data text-[13px] whitespace-nowrap">{project.slug}</TableCell>
      <TableCell className="text-[13px] whitespace-nowrap">
        {environments.isPending
          ? "Loading"
          : environments.isError
            ? "Not loaded"
            : list.map((environment) => environment.name).join(", ")}
      </TableCell>
      <TableCell className="text-[13px] whitespace-nowrap">
        {project.streams.length > 0
          ? project.streams.map((stream) => streamLabels[stream]).join(", ")
          : "None yet"}
      </TableCell>
      <TableCell className="type-data text-right text-[13px] whitespace-nowrap">
        {count(project.daily_send_cap)}
      </TableCell>
      <TableCell className="text-right">
        <StatusBadge tone={paused ? "destructive" : "success"}>
          {project.kill_switch.state === "automatic"
            ? "Paused automatically"
            : project.kill_switch.state === "manual"
              ? "Paused by hand"
              : "Sending"}
        </StatusBadge>
      </TableCell>
      <TableCell className="text-right">
        <When value={project.created_at} />
      </TableCell>
      <TableCell className="text-right whitespace-nowrap">
        {canManage ? (
          <ProjectMenu
            name={project.name}
            paused={paused}
            canAddPreview={!hasPreview}
            addingPreview={addPreview.isPending}
            onEdit={onEdit}
            onAct={onAct}
            onAddPreview={() => addPreview.mutate()}
          />
        ) : null}
      </TableCell>
    </TableRow>
  );
}

/**
 * A row's actions: the ⋯ button stays a plain button until it is pressed, then the menu (Radix,
 * in project-menu.ondemand.tsx) loads and opens at once, as the rail's menus do
 * (shell/deferred.tsx), so the page carries none of it until someone reaches for it.
 */
function ProjectMenu(props: ProjectMenuProps) {
  const [active, setActive] = useState(false);
  const button = (
    <Button
      variant="ghost"
      size="icon-sm"
      aria-label={`Actions for ${props.name}`}
      aria-expanded={false}
      onClick={() => startTransition(() => setActive(true))}
    >
      <MoreIcon size={20} />
    </Button>
  );
  return (
    <Suspense fallback={button}>{active ? <LoadedMenu {...props} defaultOpen /> : button}</Suspense>
  );
}

const LoadedMenu = lazy(() =>
  import("@/components/tenancy/project-menu.ondemand").then((m) => ({ default: m.ProjectMenu })),
);

/** Each act's countersign: what happens, to what, and the one button that does it. */
function signFor(act: Act, project: Project) {
  const subject = project.name;
  switch (act) {
    case "rotate":
      return {
        title: `Rotate the signing secret of ${subject}`,
        description:
          "Links sent from now on are signed with a new secret. Recipient links already sent keep working until the secret is rotated a second time.",
        plan: {
          label: "What rotating does",
          columns: ["Action", "Secret", "Why"] as const,
          steps: [
            {
              key: "new",
              verdict: "Create",
              subject: "A new secret",
              reason: "Signs every link from now on.",
            },
            {
              key: "current",
              verdict: "Keep",
              subject: "The current secret",
              reason: "Kept as the previous one, so links already sent keep working.",
            },
            {
              key: "previous",
              verdict: "Drop",
              subject: "The previous secret, if any",
              reason: "Links signed before the last rotation stop working.",
            },
          ],
        },
        act: "Rotate secret",
      };
    case "pause":
      return {
        title: `Pause sending for ${subject}`,
        description:
          "Its live mail is held, not failed, until someone resumes sending. Test mail is not affected.",
        plan: {
          label: "What pausing does",
          columns: ["Action", "Project", "Until"] as const,
          steps: [
            { key: project.id, verdict: "Pause", subject, reason: "Someone resumes it here." },
          ],
        },
        act: "Pause sending",
        tone: "destructive" as const,
      };
    case "resume":
      return {
        title: `Resume sending for ${subject}`,
        description:
          project.kill_switch.state === "automatic"
            ? `It paused itself: ${project.kill_switch.reason ?? "a rate crossed its limit"} Resume once the cause is fixed; the held mail goes out.`
            : "The held mail goes out, and new live mail is sent again.",
        plan: {
          label: "What resuming does",
          columns: ["Action", "Project", "Trips again at"] as const,
          steps: [
            {
              key: project.id,
              verdict: "Resume",
              subject,
              detail: project.kill_switch.since
                ? `paused ${stamp(project.kill_switch.since)} UTC`
                : undefined,
              reason: killSwitchFigures[0].value,
            },
          ],
        },
        act: "Resume sending",
      };
    case "delete":
      return {
        title: `Delete ${subject}`,
        description:
          "Its keys, templates, audience and history are deleted. A project with domains cannot be deleted until they are removed.",
        plan: {
          label: "The project to delete",
          columns: ["Action", "Project", "Created"] as const,
          steps: [
            {
              key: project.id,
              verdict: "Delete",
              subject,
              detail: project.slug,
              reason: `${stamp(project.created_at)} UTC`,
            },
          ],
        },
        act: "Delete project",
        tone: "destructive" as const,
      };
  }
}
